import { useCallback, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { getEventLogs } from "../../services";
import { EventLog } from "../../types/event-logs.type";
import { ROLE_COLOR_MAP } from "../UserManagement/data";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import EventLogsPagination from "./Pagination";
import EventLogsFilter from "./Filter";
import Typography from "@mui/material/Typography";
import { formatDate } from "../../utils/date";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

const renderSkeletonRows = () => {
  return Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: 7 }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

const LIMIT = 10;

export default function EventLogs() {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [eventLogs, setEventLogs] = useState<EventLog[]>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("all");
  const [selectedResource, setSelectedResource] = useState<string>("all");

  const onGetEventLogs = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: {
          data: { count, rows },
        },
      } = await getEventLogs(
        page,
        limit,
        startDate,
        endDate,
        selectedResource,
        selectedAction,
      );

      setEventLogs(rows);
      setTotalCount(count);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [page, limit, startDate, endDate, selectedResource, selectedAction]);

  useEffect(() => {
    onGetEventLogs();
  }, [onGetEventLogs]);

  return (
    <Paper>
      <EventLogsFilter
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction}
        selectedResource={selectedResource}
        setSelectedResource={setSelectedResource}
      />
      <TableContainer>
        <Table>
          <TableHead
            sx={{
              bgcolor: (theme) => theme.palette.background.default,
            }}
          >
            <TableRow>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                วันที่/เวลา
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ประเภทข้อมูล
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                รายละเอียด
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ชื่อผู้ใช้
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ตำแหน่ง / แผนก
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && renderSkeletonRows()}
            {!loading && eventLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              eventLogs.length > 0 &&
              eventLogs.map((event, index) => (
                <TableRow
                  key={event.id}
                  sx={{
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                  }}
                >
                  <TableCell>
                    {formatDate(event.timestamp, "DD/MM/YYYY HH:mm:ss")}
                  </TableCell>
                  <TableCell>{event.resource_type}</TableCell>
                  <TableCell>{event.detail_th}</TableCell>
                  <TableCell>{event.fullname}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {event.user_roles.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          variant="outlined"
                          color="primary"
                          sx={{
                            width: "100%",
                            textTransform: "capitalize",
                            borderColor: ROLE_COLOR_MAP[role],
                            color: ROLE_COLOR_MAP[role],
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EventLogsPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
